import React, { useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import { match } from 'variant';
import { useDebugLog } from '@wix/cli-debug-log';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { APP_CONFIG_PATH } from '@wix/cli-app-definitions';
import { useAsync, useExit, Spinner } from '@wix/cli-ui-kit';
import { readAppConfig, saveAppConfig } from '../files/app.config.js';
import { useProjectModel } from '../model.js';
import { ResolveSite } from './ResolveSite.js';
import { SiteContext } from './context.js';
import { SiteState } from './site-state.js';

export const SiteProvider: FC<{
  children: ReactNode | ((siteId: string) => ReactNode);
}> = ({ children }) => {
  const exit = useExit();
  const {
    model: { projectFolder },
  } = useProjectModel();
  const writeDebugLog = useDebugLog();

  const { status, merge } = useAsync(async () => {
    const siteId = (await readAppConfig(projectFolder, writeDebugLog))?.siteId;

    return siteId ? SiteState.Resolved({ siteId }) : SiteState.NotConfigured();
  }, []);

  const setSite = useCallback(
    async (siteId: string) => {
      try {
        await saveAppConfig(projectFolder, { siteId });
        merge({ result: SiteState.Resolved({ siteId }) });
      } catch (e) {
        throw new CliError({
          code: CliErrorCode.FailedToSaveDevelopmentSite({
            path: APP_CONFIG_PATH,
          }),
          cause: e,
        });
      }
    },
    [merge, projectFolder]
  );

  const handleResolveSite = useCallback(
    (siteId: string) => setSite(siteId).catch(exit),
    [exit, setSite]
  );

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text="Resolving development site..." />,
    Success: ({ result: state }) => {
      return match(state, {
        NotConfigured: (variant) => (
          <ResolveSite state={variant} onSubmit={handleResolveSite} />
        ),
        Resolved: ({ siteId }) => (
          <SiteContext.Provider
            value={{
              siteId,
              setSite,
            }}
          >
            {typeof children === 'function' ? children(siteId) : children}
          </SiteContext.Provider>
        ),
      });
    },
  });
};
